import { For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

interface SessionRow {
  code: string;
  created_at: string;
  updated_at: string;
  support_approved: boolean;
  control_granted: boolean;
  native_agent_online: boolean;
  client_id: string | null;
  tenant_id: string | null;
  peer_count: number;
}

interface ClientRow {
  id: string;
  name: string;
  slug: string;
  tenant_id: string;
  created_at: string;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

export function SuperAdminClientsSessionsPage() {
  const sessionsQ = createQuery<{ ok: boolean; sessions: SessionRow[] }>(() => ({
    queryKey: ["super-admin-sessions"],
    queryFn: () => jget<{ ok: boolean; sessions: SessionRow[] }>("/api/super-admin/sessions"),
    refetchInterval: 5000,
  }));

  const clientsQ = createQuery<{ ok: boolean; clients: ClientRow[] }>(() => ({
    queryKey: ["super-admin-clients"],
    queryFn: () => jget<{ ok: boolean; clients: ClientRow[] }>("/api/super-admin/clients"),
    refetchInterval: 15_000,
  }));

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Clients & Sessions
          <span class="plugins-console-sub">vue cross-tenant</span>
        </span>
      </div>

      <Show when={sessionsQ.error || clientsQ.error}>
        <div class="bug-report-message" data-kind="err" style={{ "margin-bottom": "10px" }}>
          {sessionsQ.error instanceof Error
            ? sessionsQ.error.message
            : clientsQ.error instanceof Error
              ? clientsQ.error.message
              : String(sessionsQ.error || clientsQ.error)}
        </div>
      </Show>

      <div style={{ display: "grid", gap: "14px", "margin-bottom": "14px" }}>
        <div class="rmm-table-wrap">
          <div style={{ padding: "8px 10px", "font-weight": "600", "font-size": "13px" }}>Sessions</div>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Peers</th>
                <th>Approuvé</th>
                <th>Contrôle</th>
                <th>Agent</th>
                <th>Client</th>
                <th>Tenant</th>
                <th>Créée</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={!sessionsQ.isLoading}
                fallback={
                  <tr>
                    <td colSpan={8}>Chargement…</td>
                  </tr>
                }
              >
                <For each={sessionsQ.data?.sessions || []}>
                  {(s) => (
                    <tr>
                      <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{s.code.slice(0, 12)}…</td>
                      <td style={{ "font-size": "11px" }}>{s.peer_count}</td>
                      <td>
                        <span
                          style={{
                            "font-size": "10px",
                            padding: "1px 6px",
                            "border-radius": "4px",
                            background: s.support_approved ? "rgba(74,222,128,0.12)" : "rgba(148,163,184,0.12)",
                            color: s.support_approved ? "var(--ok, #4ade80)" : "var(--text-4)",
                          }}
                        >
                          {s.support_approved ? "oui" : "non"}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            "font-size": "10px",
                            padding: "1px 6px",
                            "border-radius": "4px",
                            background: s.control_granted ? "rgba(74,222,128,0.12)" : "rgba(148,163,184,0.12)",
                            color: s.control_granted ? "var(--ok, #4ade80)" : "var(--text-4)",
                          }}
                        >
                          {s.control_granted ? "oui" : "non"}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            "font-size": "10px",
                            padding: "1px 6px",
                            "border-radius": "4px",
                            background: s.native_agent_online ? "rgba(74,222,128,0.12)" : "rgba(148,163,184,0.12)",
                            color: s.native_agent_online ? "var(--ok, #4ade80)" : "var(--text-4)",
                          }}
                        >
                          {s.native_agent_online ? "online" : "offline"}
                        </span>
                      </td>
                      <td style={{ "font-size": "11px" }}>{s.client_id ? s.client_id.slice(0, 8) : "—"}</td>
                      <td style={{ "font-size": "11px" }}>{s.tenant_id ? s.tenant_id.slice(0, 8) : "—"}</td>
                      <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                    </tr>
                  )}
                </For>
                <Show when={(sessionsQ.data?.sessions || []).length === 0}>
                  <tr>
                    <td colSpan={8} style={{ color: "var(--text-4)", "text-align": "center" }}>
                      Aucune session.
                    </td>
                  </tr>
                </Show>
              </Show>
            </tbody>
          </table>
        </div>

        <div class="rmm-table-wrap">
          <div style={{ padding: "8px 10px", "font-weight": "600", "font-size": "13px" }}>Clients</div>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Slug</th>
                <th>Tenant</th>
                <th>Créé</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={!clientsQ.isLoading}
                fallback={
                  <tr>
                    <td colSpan={4}>Chargement…</td>
                  </tr>
                }
              >
                <For each={clientsQ.data?.clients || []}>
                  {(c) => (
                    <tr>
                      <td style={{ "font-weight": "500" }}>{c.name}</td>
                      <td style={{ "font-size": "11px", color: "var(--text-4)" }}>{c.slug}</td>
                      <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                        {c.tenant_id.slice(0, 8)}
                      </td>
                      <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  )}
                </For>
                <Show when={(clientsQ.data?.clients || []).length === 0}>
                  <tr>
                    <td colSpan={4} style={{ color: "var(--text-4)", "text-align": "center" }}>
                      Aucun client.
                    </td>
                  </tr>
                </Show>
              </Show>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
